import { useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { t, tEn } from '../i18n/translations'
import { KIDASE_PRICES, upgradeUrl } from '../lib/access'
import { startTrial, trialErrorMessage } from '../lib/trial'
import TeacherCodeForm from './TeacherCodeForm'

// Shown when a user without Premium opens a locked lesson.
//
// It offers the 30-day trial first, subscription plans second, and a teacher
// code for those who have one. It grants nothing itself: the trial is started
// by the account function, and the entitlement listener in AuthContext turns
// Premium on.
//
// Signing in must never start a trial by itself. Sign-in only changes what
// this dialog shows; the trial begins when the button is pressed.
export default function UpgradePrompt({ onClose }) {
  const { user, login, isPremium, canStartTrial, trialUsed, refreshTrialStatus } = useAuth()
  const [showCode, setShowCode] = useState(false)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState('')

  // Premium arrived (a subscriber signed in, or the trial just started):
  // nothing left to offer.
  useEffect(() => { if (isPremium) onClose() }, [isPremium, onClose])
  if (isPremium) return null

  const price = `${KIDASE_PRICES.monthly}/${t('perMonth')} ${t('or')} ${KIDASE_PRICES.annual}/${t('perYear')}`

  async function activate() {
    if (starting) return
    setStarting(true)
    setError('')
    try {
      await startTrial()
      await refreshTrialStatus()
      // The entitlement listener closes this dialog once Premium lands.
    } catch (err) {
      setError(trialErrorMessage(err))
      await refreshTrialStatus()
      setStarting(false)
    }
  }

  return (
    <div className="upgrade-overlay" onClick={onClose}>
      <div className="upgrade-prompt" role="dialog" aria-modal="true" aria-labelledby="upgrade-title" onClick={e => e.stopPropagation()}>
        <button className="upgrade-close" onClick={onClose} aria-label={t('continueFree')}>&#10005;</button>
        <h3 className="upgrade-title" id="upgrade-title" title={tEn('trialInviteTitle')}>{t('trialInviteTitle')}</h3>
        {showCode ? (
          <TeacherCodeForm onDone={() => setShowCode(false)} />
        ) : (
          <>
            <p className="upgrade-desc" title={tEn('trialInviteDesc')}>{t('trialInviteDesc')}</p>

            {error && <p className="teacher-code-error" role="alert">{error}</p>}

            {/* The trial is the primary offer while it is available. Someone
                who has used it sees why it is gone, not a dead button. */}
            {canStartTrial && (
              <button className="upgrade-cta" onClick={activate} disabled={starting} title={tEn('trialStart')}>
                {starting ? t('trialStarting') : t('trialStart')}
              </button>
            )}
            {!user && (
              <button className="upgrade-cta" onClick={login} title={tEn('trialStart')}>
                {t('trialStart')}
              </button>
            )}
            {user && trialUsed && (
              <p className="trial-used" title={tEn('trialUsed')}>{t('trialUsed')}</p>
            )}

            <p className="upgrade-plan">
              <strong>Kidase Individual</strong>
              <span className="upgrade-price">{price}</span>
            </p>
            <a className="upgrade-secondary" href={upgradeUrl('locked-lesson')} title={tEn('viewPlans')}>
              {t('viewPlans')}
            </a>
            <button className="upgrade-secondary" onClick={onClose} title={tEn('continueFree')}>
              {t('continueFree')}
            </button>
            {/* Teachers given a code by Geezword redeem it here rather than paying. */}
            <button className="upgrade-signin" onClick={() => setShowCode(true)} title={tEn('teacherCodeLink')}>
              {t('teacherCodeLink')}
            </button>
            {!user && (
              <button className="upgrade-signin" onClick={login} title={tEn('alreadySubscribed')}>
                {t('alreadySubscribed')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
